import type {
  AudioStreamInfo,
  H264Profile,
  ProfileId,
  TranscodeProfile,
  Verdict,
  VerdictAxis,
  VerdictStatus,
  VideoProbeReport,
  VideoStreamInfo,
} from './types';

export const AR_MOBILE: TranscodeProfile = {
  id: 'AR-mobile',
  label: 'AR mobile',
  description: 'Recommended default. Balances quality and load time on mobile networks.',
  container: 'mp4',
  video: {
    codec: 'h264',
    maxWidth: 1280,
    maxHeight: 720,
    targetBitrateBps: 1_000_000,
    maxFramerate: 30,
    profile: 'High',
    maxLevel: '4.1',
    faststart: true,
  },
  audio: {
    codec: 'aac',
    maxBitrateBps: 96_000,
    maxChannels: 2,
    maxSampleRateHz: 48_000,
  },
  attention: {
    maxDurationSec: 60,
    maxFileSizeBytes: 10 * 1024 * 1024,
  },
};

export const AR_MOBILE_HQ: TranscodeProfile = {
  id: 'AR-mobile-hq',
  label: 'AR mobile (HQ)',
  description: 'Higher fidelity for detail-heavy content. Assumes viewers are on WiFi or strong mobile data.',
  container: 'mp4',
  video: {
    codec: 'h264',
    maxWidth: 1920,
    maxHeight: 1080,
    targetBitrateBps: 2_500_000,
    maxFramerate: 30,
    profile: 'High',
    maxLevel: '4.1',
    faststart: true,
  },
  audio: {
    codec: 'aac',
    maxBitrateBps: 128_000,
    maxChannels: 2,
    maxSampleRateHz: 48_000,
  },
  attention: {
    maxDurationSec: 60,
    maxFileSizeBytes: 20 * 1024 * 1024,
  },
};

export const AR_LITE: TranscodeProfile = {
  id: 'AR-lite',
  label: 'AR lite',
  description: 'Aggressive compression for slow networks or always-on ambient content.',
  container: 'mp4',
  video: {
    codec: 'h264',
    maxWidth: 960,
    maxHeight: 540,
    targetBitrateBps: 600_000,
    maxFramerate: 30,
    profile: 'Main',
    maxLevel: '3.1',
    faststart: true,
  },
  audio: {
    codec: 'aac',
    maxBitrateBps: 64_000,
    maxChannels: 2,
    maxSampleRateHz: 48_000,
  },
  attention: {
    maxDurationSec: 60,
    maxFileSizeBytes: 6 * 1024 * 1024,
  },
};

export const CUSTOM_DEFAULT: TranscodeProfile = {
  id: 'Custom',
  label: 'Custom',
  description: 'Edit the targets below to match your needs.',
  container: 'mp4',
  video: {
    codec: 'h264',
    maxWidth: 1280,
    maxHeight: 720,
    targetBitrateBps: 1_000_000,
    maxFramerate: 30,
    profile: 'High',
    maxLevel: '4.1',
    faststart: true,
  },
  audio: {
    codec: 'aac',
    maxBitrateBps: 96_000,
    maxChannels: 2,
    maxSampleRateHz: 48_000,
  },
  attention: null,
};

export const BUILT_IN_PROFILES: readonly TranscodeProfile[] = [
  AR_MOBILE,
  AR_MOBILE_HQ,
  AR_LITE,
  CUSTOM_DEFAULT,
];

export function getProfileById(id: ProfileId): TranscodeProfile | null {
  return BUILT_IN_PROFILES.find(p => p.id === id) ?? null;
}

const H264_PROFILE_ORDER: Record<H264Profile, number> = {
  Baseline: 0,
  Main: 1,
  High: 2,
};

function parseLevel(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function worst(statuses: VerdictStatus[]): VerdictStatus {
  if (statuses.includes('red')) return 'red';
  if (statuses.includes('yellow')) return 'yellow';
  return 'green';
}

function fmtBps(bps: number | null): string {
  if (bps == null) return 'unknown';
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  return `${Math.round(bps / 1000)} kbps`;
}

function fmtRes(w: number, h: number): string {
  return `${w}×${h}`;
}

function evalContainer(report: VideoProbeReport, profile: TranscodeProfile): VerdictAxis {
  const actual = report.container.format;
  if (report.bailout === 'fragmented-mp4') {
    return {
      axis: 'container',
      status: 'red',
      actual: `${actual} (fragmented)`,
      target: profile.container,
      explanation: 'Fragmented MP4 is not supported by the viewer. Remux to progressive MP4.',
    };
  }
  const matches = actual === profile.container || (profile.container === 'mp4' && (actual === 'mov' || actual === 'm4v'));
  return {
    axis: 'container',
    status: matches ? 'green' : 'red',
    actual,
    target: profile.container,
    explanation: matches
      ? 'Container is compatible.'
      : `Container must be ${profile.container} for broad web playback.`,
  };
}

function evalFaststart(report: VideoProbeReport, profile: TranscodeProfile): VerdictAxis {
  const actual = report.container.faststart;
  return {
    axis: 'faststart',
    status: actual ? 'green' : 'red',
    actual: actual ? 'yes' : 'no',
    target: profile.video.faststart ? 'yes' : 'no',
    explanation: actual
      ? 'moov atom is at the start — video can start playing before fully downloaded.'
      : 'moov atom is at the end — viewers must download the whole file before playback starts. Rewrite with +faststart.',
  };
}

function evalCodec(video: VideoStreamInfo, profile: TranscodeProfile): VerdictAxis {
  const actual = video.codec;
  const target = profile.video.codec;
  if (actual === target) {
    return {
      axis: 'codec',
      status: 'green',
      actual,
      target,
      explanation: 'Codec is universally supported.',
    };
  }
  if (actual === 'hevc') {
    return {
      axis: 'codec',
      status: 'yellow',
      actual,
      target,
      explanation:
        'HEVC plays on iOS and modern Androids but fails for roughly 10–15% of viewers — older Android phones (2018–2020 budget ARCore devices) and Windows desktop users without the HEVC codec extension. Transcode to H.264 for universal compatibility.',
    };
  }
  if (actual === 'av1' || actual === 'vp9') {
    return {
      axis: 'codec',
      status: 'red',
      actual,
      target,
      explanation: `${actual.toUpperCase()} is not broadly supported for <video> playback across Safari and older Android. Transcode to H.264.`,
    };
  }
  return {
    axis: 'codec',
    status: 'red',
    actual,
    target,
    explanation: `Codec is not ${target}; cross-browser playback is not guaranteed.`,
  };
}

function evalCodecFourcc(video: VideoStreamInfo): VerdictAxis | null {
  if (video.codec !== 'hevc') return null;
  const fourcc = video.codecFourcc.toLowerCase();
  if (fourcc === 'hev1') {
    return {
      axis: 'codecFourcc',
      status: 'red',
      actual: 'hev1',
      target: 'hvc1',
      explanation: 'HEVC tagged as "hev1" does not play on Safari. Remux with hvc1 tag (ffmpeg -tag:v hvc1).',
    };
  }
  if (fourcc === 'hvc1') {
    return {
      axis: 'codecFourcc',
      status: 'green',
      actual: 'hvc1',
      target: 'hvc1',
      explanation: 'HEVC is tagged correctly for Safari playback.',
    };
  }
  return null;
}

function evalProfile(video: VideoStreamInfo, profile: TranscodeProfile): VerdictAxis | null {
  if (video.codec !== 'h264' || profile.video.codec !== 'h264') return null;
  const actual = video.profile;
  if (actual == null) {
    return {
      axis: 'profile',
      status: 'yellow',
      actual: 'unknown',
      target: profile.video.profile,
      explanation: 'Could not determine H.264 profile.',
    };
  }
  const actualRank = H264_PROFILE_ORDER[actual as H264Profile];
  const targetRank = H264_PROFILE_ORDER[profile.video.profile];
  if (actualRank == null) {
    return {
      axis: 'profile',
      status: 'yellow',
      actual,
      target: profile.video.profile,
      explanation: `Unusual H.264 profile "${actual}" — may have decode issues on some devices.`,
    };
  }
  if (actualRank <= targetRank) {
    return {
      axis: 'profile',
      status: 'green',
      actual,
      target: profile.video.profile,
      explanation: 'H.264 profile is within the supported range.',
    };
  }
  return {
    axis: 'profile',
    status: 'red',
    actual,
    target: profile.video.profile,
    explanation: `H.264 ${actual} exceeds the target profile; older devices may fail to decode.`,
  };
}

function evalLevel(video: VideoStreamInfo, profile: TranscodeProfile): VerdictAxis | null {
  if (video.codec !== 'h264') return null;
  const actual = video.level;
  if (actual == null) {
    return {
      axis: 'level',
      status: 'yellow',
      actual: 'unknown',
      target: profile.video.maxLevel,
      explanation: 'Could not determine H.264 level.',
    };
  }
  const actualN = parseLevel(actual);
  const targetN = parseLevel(profile.video.maxLevel);
  return {
    axis: 'level',
    status: actualN <= targetN ? 'green' : 'red',
    actual,
    target: `≤ ${profile.video.maxLevel}`,
    explanation:
      actualN <= targetN
        ? 'H.264 level is within spec for the target devices.'
        : `Level ${actual} exceeds ${profile.video.maxLevel}; older hardware decoders may reject it.`,
  };
}

function evalResolution(video: VideoStreamInfo, profile: TranscodeProfile): VerdictAxis {
  const { width, height } = video;
  const { maxWidth, maxHeight } = profile.video;
  const actualStr = fmtRes(width, height);
  const targetStr = `≤ ${fmtRes(maxWidth, maxHeight)}`;
  const longest = Math.max(width, height);
  const targetLongest = Math.max(maxWidth, maxHeight);
  if (longest <= targetLongest) {
    return { axis: 'resolution', status: 'green', actual: actualStr, target: targetStr, explanation: 'Resolution is at or below the target.' };
  }
  if (longest <= targetLongest * 1.51) {
    return {
      axis: 'resolution',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: 'Moderately over target (e.g. 1080p against 720p). Downscaling recommended to save bandwidth.',
    };
  }
  return {
    axis: 'resolution',
    status: 'red',
    actual: actualStr,
    target: targetStr,
    explanation: `Resolution is well above the target; viewers waste bandwidth on pixels they cannot see in AR.`,
  };
}

function evalBitrate(video: VideoStreamInfo, profile: TranscodeProfile): VerdictAxis {
  const actual = video.bitrateBps;
  const target = profile.video.targetBitrateBps;
  if (actual == null) {
    return {
      axis: 'bitrate',
      status: 'yellow',
      actual: 'unknown',
      target: fmtBps(target),
      explanation: 'Bitrate could not be determined from the file.',
    };
  }
  if (actual <= target * 1.1) {
    return { axis: 'bitrate', status: 'green', actual: fmtBps(actual), target: fmtBps(target), explanation: 'Bitrate is at or near the target.' };
  }
  if (actual <= target * 1.75) {
    return {
      axis: 'bitrate',
      status: 'yellow',
      actual: fmtBps(actual),
      target: fmtBps(target),
      explanation: 'Bitrate is above target. Re-encoding would meaningfully improve load time.',
    };
  }
  return {
    axis: 'bitrate',
    status: 'red',
    actual: fmtBps(actual),
    target: fmtBps(target),
    explanation: 'Bitrate is far above target. Expect slow starts and stalls on mobile networks.',
  };
}

/**
 * Floor below which client playback may stutter regardless of profile. This is
 * a playback-smoothness concern, not a bitrate-budget one, so it applies across
 * every profile rather than being a per-profile field.
 */
const MIN_FRAMERATE = 10;

function evalFramerate(video: VideoStreamInfo, profile: TranscodeProfile): VerdictAxis {
  const actual = video.framerate;
  const target = profile.video.maxFramerate;
  const targetStr = `${MIN_FRAMERATE}–${target} fps`;
  if (actual == null) {
    // No measured value — the lower stutter floor is meaningless here, so show
    // only the upper bound rather than the full range.
    return { axis: 'framerate', status: 'yellow', actual: 'unknown', target: `≤ ${target} fps`, explanation: 'Framerate could not be determined.' };
  }
  const actualStr = `${actual.toFixed(0)} fps`;
  if (actual < MIN_FRAMERATE) {
    return {
      axis: 'framerate',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: `Framerate below ${MIN_FRAMERATE} fps may stutter on some clients; re-encode at 24–30 fps for smooth playback.`,
    };
  }
  if (actual <= target) {
    return { axis: 'framerate', status: 'green', actual: actualStr, target: targetStr, explanation: 'Framerate is within the target range.' };
  }
  if (actual <= target * 1.5) {
    return {
      axis: 'framerate',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: 'Higher framerate plays fine but doubles the bitrate budget for marginal perceived gain in AR.',
    };
  }
  return {
    axis: 'framerate',
    status: 'yellow',
    actual: actualStr,
    target: targetStr,
    explanation: 'Very high framerate wastes bandwidth; reduce to 30 fps for AR.',
  };
}

function evalPixelAspect(video: VideoStreamInfo): VerdictAxis | null {
  if (!video.isAnamorphic) {
    if (video.sampleAspectRatio == null) return null;
    return {
      axis: 'pixelAspect',
      status: 'green',
      actual: `SAR 1:1 (square)`,
      target: 'square pixels',
      explanation: 'Pixels are square — display aspect matches coded dimensions.',
    };
  }
  return {
    axis: 'pixelAspect',
    status: 'red',
    actual: `SAR ${video.sampleAspectRatio ?? '?'} (anamorphic)`,
    target: 'SAR 1:1 (square)',
    explanation:
      'Non-square pixels. Unity VideoPlayer on Android ignores the pixel-aspect flag and renders the coded dimensions, producing stretched or squished output. Re-encode with the display aspect baked in so the coded dimensions match the display (SAR 1:1).',
  };
}

function evalEvenDimensions(video: VideoStreamInfo): VerdictAxis | null {
  const w = video.codedWidth ?? video.width;
  const h = video.codedHeight ?? video.height;
  if (!w || !h) return null;
  const even = w % 2 === 0 && h % 2 === 0;
  if (even) return null;
  return {
    axis: 'evenDimensions',
    status: 'red',
    actual: `${w}×${h}`,
    target: 'even dimensions',
    explanation:
      'H.264 with yuv420p chroma requires even width and height. Odd dimensions cause some decoders to reject the stream or silently crop. Resize to nearest even numbers.',
  };
}

function evalChromaSubsampling(video: VideoStreamInfo): VerdictAxis | null {
  if (video.chromaSubsampling === 'yuv420p') {
    return {
      axis: 'chromaSubsampling',
      status: 'green',
      actual: 'yuv420p',
      target: 'yuv420p',
      explanation: 'Universally hardware-decoded.',
    };
  }
  if (video.chromaSubsampling === 'unknown') return null;
  return {
    axis: 'chromaSubsampling',
    status: 'red',
    actual: video.chromaSubsampling,
    target: 'yuv420p',
    explanation:
      `${video.chromaSubsampling} chroma subsampling is rarely hardware-decoded on Android and falls back to software or fails outright. Re-encode to yuv420p.`,
  };
}

function evalBitDepth(video: VideoStreamInfo): VerdictAxis | null {
  if (video.bitDepth === 8) return null;
  if (video.bitDepth == null) return null;
  return {
    axis: 'bitDepth',
    status: 'yellow',
    actual: `${video.bitDepth}-bit`,
    target: '8-bit',
    explanation:
      `${video.bitDepth}-bit video often falls back to software decode on mobile. Re-encode to 8-bit (yuv420p) unless you specifically need HDR.`,
  };
}

function evalColorRange(video: VideoStreamInfo): VerdictAxis | null {
  if (video.colorRange !== 'pc') return null;
  return {
    axis: 'colorRange',
    status: 'yellow',
    actual: 'pc (full range)',
    target: 'tv (limited range)',
    explanation:
      'Output is tagged full-range (the WebCodecs default for canvas-derived frames). Modern iOS Safari and Android Chrome play it correctly; a minority of older Android MediaCodec builds ignore the flag and crush blacks. For maximum compatibility, transcode with the local ffmpeg command above (which forces -color_range tv).',
  };
}

function evalDuration(report: VideoProbeReport, profile: TranscodeProfile): VerdictAxis | null {
  const target = profile.attention;
  if (!target) return null;
  const actual = report.durationSec || report.video?.durationSec || 0;
  if (actual <= 0) return null;
  const actualStr = `${actual.toFixed(1)} s`;
  const targetStr = `≤ ${target.maxDurationSec} s`;
  if (actual <= target.maxDurationSec) {
    return {
      axis: 'duration',
      status: 'green',
      actual: actualStr,
      target: targetStr,
      explanation: 'Duration is within the suggested attention budget.',
    };
  }
  if (actual <= target.maxDurationSec * 2) {
    return {
      axis: 'duration',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: `AR viewers fatigue quickly. ≤ ${target.maxDurationSec} s holds attention best; consider trimming.`,
    };
  }
  return {
    axis: 'duration',
    status: 'yellow',
    actual: actualStr,
    target: targetStr,
    explanation: `Long clip — AR viewers rarely watch more than ${target.maxDurationSec} s in one sitting. Consider splitting into shorter segments.`,
  };
}

function evalFileSize(report: VideoProbeReport, profile: TranscodeProfile): VerdictAxis | null {
  const target = profile.attention;
  if (!target) return null;
  const actual = report.container.fileSize;
  if (!actual) return null;
  const actualStr = formatBytes(actual);
  const targetStr = `≤ ${formatBytes(target.maxFileSizeBytes)}`;
  if (actual <= target.maxFileSizeBytes) {
    return {
      axis: 'fileSize',
      status: 'green',
      actual: actualStr,
      target: targetStr,
      explanation: 'File size will load quickly on mobile networks.',
    };
  }
  if (actual <= target.maxFileSizeBytes * 2) {
    return {
      axis: 'fileSize',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: 'Moderately large for AR delivery. Expect slower first-paint on mobile networks.',
    };
  }
  return {
    axis: 'fileSize',
    status: 'yellow',
    actual: actualStr,
    target: targetStr,
    explanation: 'Large file — mobile viewers may abandon while waiting. Consider a shorter clip or a lower-bitrate profile.',
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function evalAudio(audio: AudioStreamInfo | null, profile: TranscodeProfile): VerdictAxis {
  const target = profile.audio;
  if (target == null) {
    if (audio == null) {
      return { axis: 'audio', status: 'green', actual: 'none', target: 'none', explanation: 'No audio, as targeted.' };
    }
    return {
      axis: 'audio',
      status: 'yellow',
      actual: `${audio.codec} ${audio.channels}ch`,
      target: 'none',
      explanation: 'Profile strips audio. If you need audio, pick a different profile or use Custom.',
    };
  }
  if (audio == null) {
    return {
      axis: 'audio',
      status: 'yellow',
      actual: 'none',
      target: `${target.codec} ≤ ${Math.round(target.maxBitrateBps / 1000)} kbps`,
      explanation: 'No audio track. Profile allows audio; if intentional, ignore this.',
    };
  }
  const codecOk = audio.codec === target.codec;
  const bitrateOk = audio.bitrateBps == null || audio.bitrateBps <= target.maxBitrateBps * 1.25;
  const channelsOk = audio.channels <= target.maxChannels;
  if (codecOk && bitrateOk && channelsOk) {
    return {
      axis: 'audio',
      status: 'green',
      actual: `${audio.codec} ${audio.channels}ch ${fmtBps(audio.bitrateBps)}`,
      target: `${target.codec} ≤ ${target.maxChannels}ch ${fmtBps(target.maxBitrateBps)}`,
      explanation: 'Audio is within target.',
    };
  }
  return {
    axis: 'audio',
    status: 'yellow',
    actual: `${audio.codec} ${audio.channels}ch ${fmtBps(audio.bitrateBps)}`,
    target: `${target.codec} ≤ ${target.maxChannels}ch ${fmtBps(target.maxBitrateBps)}`,
    explanation: !codecOk
      ? `Audio codec is ${audio.codec}, not ${target.codec}. Transcode for broad compatibility.`
      : !channelsOk
        ? 'Channel count exceeds target; downmix to stereo.'
        : 'Audio bitrate is above target.',
  };
}

export function evaluateAgainstProfile(report: VideoProbeReport, profile: TranscodeProfile): Verdict {
  const axes: VerdictAxis[] = [];
  axes.push(evalContainer(report, profile));
  axes.push(evalFaststart(report, profile));

  if (report.video) {
    axes.push(evalCodec(report.video, profile));
    const fourccAxis = evalCodecFourcc(report.video);
    if (fourccAxis) axes.push(fourccAxis);
    const profileAxis = evalProfile(report.video, profile);
    if (profileAxis) axes.push(profileAxis);
    const levelAxis = evalLevel(report.video, profile);
    if (levelAxis) axes.push(levelAxis);
    axes.push(evalResolution(report.video, profile));
    axes.push(evalBitrate(report.video, profile));
    axes.push(evalFramerate(report.video, profile));
    const pixelAspectAxis = evalPixelAspect(report.video);
    if (pixelAspectAxis) axes.push(pixelAspectAxis);
    const evenAxis = evalEvenDimensions(report.video);
    if (evenAxis) axes.push(evenAxis);
    const chromaAxis = evalChromaSubsampling(report.video);
    if (chromaAxis) axes.push(chromaAxis);
    const bitDepthAxis = evalBitDepth(report.video);
    if (bitDepthAxis) axes.push(bitDepthAxis);
    const rangeAxis = evalColorRange(report.video);
    if (rangeAxis) axes.push(rangeAxis);
  } else {
    axes.push({
      axis: 'codec',
      status: 'red',
      actual: 'no video stream',
      target: profile.video.codec,
      explanation: 'No video stream detected.',
    });
  }

  axes.push(evalAudio(report.audio, profile));

  const durationAxis = evalDuration(report, profile);
  if (durationAxis) axes.push(durationAxis);
  const fileSizeAxis = evalFileSize(report, profile);
  if (fileSizeAxis) axes.push(fileSizeAxis);

  return {
    overall: worst(axes.map(a => a.status)),
    axes,
  };
}
